import { endpoints } from '../../api';
import type { UserAvatar } from '../../api/people';
import type { Voice } from '../../api/types';
import { studioFields, type StudioForm } from './studio-api';
import { studioJsonKeys, validJsonValue, validShoot } from './studio-command';
import { videoDefaults } from '../video/video-model';
import { motionDefaults } from '../motion/motion-model';
import { textDefaults } from '../text/native/text-model';

/** Повтор заполняет только актуальные поля и заново проверяет доступность собственных файлов/голосов. */
export async function studioPrefill(
  value: StudioForm,
  source: Record<string, unknown> | undefined,
  avatar: UserAvatar | null,
  signal: AbortSignal,
): Promise<{ inputs: Record<string, string>; uploadIds: Record<string, string>; voice: Voice | null; unavailableFile: boolean }> {
  let voice: Voice | null = null;
  const fresh: Record<string, string> = {};
  const retained: Record<string, string> = {};
  let unavailableFile = false;
  // Восстанавливаем только поля нынешней опубликованной формы и повторно проверяем срок файлов.
  const jsonKeys = studioJsonKeys(value);
  await Promise.all(
    studioFields(value).map(async field => {
      if (field.key === 'consent_accepted') return;
      if (field.key === 'consent_version' && field.values.length === 1) {
        fresh[field.key] = field.values[0];
        return;
      }
      // Выбранный вариант «Вы» ставится только туда, где экран ждёт одного героя: кружок, движения, первый слот людей «Фото»/«Видео».
      // У остальных экранов (рилс, кавер, фотосессии) поле cast — свой состав ролей: его восстанавливает «Повторить», а не вариант.
      const own = value.constraints?.circle || value.constraints?.motion || value.constraints?.photo || value.constraints?.video;
      if (field.type === 'cast' && avatar && own) {
        if (value.constraints?.motion || value.constraints?.circle || field.key === 'cast_1') fresh[field.key] = `user_avatar:${avatar.id}`;
        return;
      }
      const raw = source?.[field.key];
      // Поля с JSON-значением (docs/59): «Повторить» приносит объект или массив — в форме они живут JSON-строкой.
      if (jsonKeys.includes(field.key)) {
        let parsed: unknown = raw;
        if (typeof raw === 'string')
          try {
            parsed = JSON.parse(raw);
          } catch {
            return;
          }
        if (raw !== undefined && raw !== null && validJsonValue(parsed)) {
          const encoded = JSON.stringify(parsed);
          if (encoded.length <= 64 * 1024) fresh[field.key] = encoded;
        }
        return;
      }
      const previous = typeof raw === 'number' || typeof raw === 'boolean' ? String(raw) : raw;
      if (typeof previous !== 'string') return;
      if (field.upload) {
        if (!/^[a-f0-9-]{36}$/i.test(previous)) {
          unavailableFile = true;
          return;
        }
        try {
          const slot = await endpoints.uploadStatus(previous, signal);
          if (slot.status !== 'ready') {
            unavailableFile = true;
            return;
          }
          fresh[field.key] = slot.id;
          retained[field.key] = slot.id;
        } catch {
          unavailableFile = true;
        }
      } else if (field.type === 'voice') {
        const id = Number(previous);
        if (Number.isSafeInteger(id) && id > 0) {
          try {
            const selected = await endpoints.voiceDetails(id, signal);
            const needsS2s = value.screen === 'voice' || field.key === value.constraints?.circle?.audio_voice_field;
            if (needsS2s && !selected.is_s2s) return;
            if (!signal.aborted) {
              voice = selected;
              fresh[field.key] = String(id);
            }
          } catch {
            /* Удалённый голос выбирается заново. */
          }
        }
      } else if ((!field.values.length || field.values.includes(previous)) && previous.length <= (field.max_length ?? 8000))
        fresh[field.key] = previous;
    }),
  );

  const circle = value.constraints?.circle;
  if (circle && !fresh.format && value.fields.some(field => field.key === 'format' && field.values.includes(circle.default_format)))
    fresh.format = circle.default_format;
  const photo = value.constraints?.photo;
  if (photo?.policy_version === 'photo-n8n-v1') {
    for (const [fieldKey, defaultKey] of [
      ['style', 'default_style_id'],
      ['format', 'default_format'],
      ['count', 'default_count'],
    ] as const) {
      const preset = photo[defaultKey];
      const choice = typeof preset === 'string' || typeof preset === 'number' ? String(preset) : '';
      if (!fresh[fieldKey] && choice && value.fields.some(field => field.key === fieldKey && field.values.includes(choice)))
        fresh[fieldKey] = choice;
    }
  }
  if (value.constraints?.photo && fresh.reference) fresh.style = 'none';
  if (value.constraints?.video) {
    for (const [key, preset] of Object.entries(videoDefaults(value))) if (!fresh[key]) fresh[key] = preset;
    if (fresh.reference) fresh.style = 'none';
  }
  if (value.constraints?.motion) for (const [key, preset] of Object.entries(motionDefaults(value))) if (!fresh[key]) fresh[key] = preset;
  if (value.constraints?.text || value.constraints?.transcription)
    for (const [key, preset] of Object.entries(textDefaults(value))) if (!fresh[key]) fresh[key] = preset;
  const shootPolicy =
    (value as StudioForm & { shoot?: unknown }).shoot ??
    ((value as StudioForm & { settings?: { shoot?: unknown } }).settings?.shoot ?? null);
  if (shootPolicy && ['photo', 'video', 'shoot', 'pshoot'].includes(value.screen) && source?.shoot !== undefined && !fresh.shoot) {
    try {
      const selection = typeof source.shoot === 'string' ? JSON.parse(source.shoot) : source.shoot;
      if (validShoot(selection, value.screen)) fresh.shoot = JSON.stringify(selection);
    } catch {
      /* Неверный черновик съёмки выбирается заново. */
    }
  }
  return { inputs: fresh, uploadIds: retained, voice, unavailableFile };
}
